import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DigestKind } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type {
  DigestDetail,
  DigestPreferences,
  DigestSummary,
  OrgDigestConfig,
  OrgNotificationDestination,
} from './types';

const jsonHeaders = { 'content-type': 'application/json' };

export interface DigestsPage {
  data: DigestSummary[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export function useDigests(page: number, pageSize = 10) {
  return useQuery({
    queryKey: ['digests', page, pageSize],
    staleTime: 30_000,
    queryFn: async (): Promise<DigestsPage> => {
      const res = await fetch(`${API_URL}/digests?page=${page}&pageSize=${pageSize}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load digests');
      const body = (await res.json()) as {
        ok: boolean;
        data: DigestSummary[];
        pagination: DigestsPage['pagination'];
      };
      return { data: body.data, pagination: body.pagination };
    },
  });
}

export function useDigest(id: string | undefined) {
  return useQuery({
    queryKey: ['digest', id],
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async (): Promise<DigestDetail> => {
      const res = await fetch(`${API_URL}/digests/${id}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load digest');
      const body = (await res.json()) as { ok: boolean; data: DigestDetail };
      return body.data;
    },
  });
}

export function useDigestConfigs() {
  return useQuery({
    queryKey: ['digest-configs'],
    staleTime: 30_000,
    queryFn: async (): Promise<OrgDigestConfig[]> => {
      const res = await fetch(`${API_URL}/org/digests/config`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load digest configuration');
      const body = (await res.json()) as { ok: boolean; data: OrgDigestConfig[] };
      return body.data;
    },
  });
}

export interface UpdateDigestConfigInput {
  kind: DigestKind;
  enabled: boolean;
  cronPattern: string;
  timezone: string;
  destinations: OrgNotificationDestination[];
}

export function useUpdateDigestConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, ...body }: UpdateDigestConfigInput): Promise<OrgDigestConfig> => {
      const res = await fetch(`${API_URL}/org/digests/config/${kind}`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save digest configuration');
      const parsed = (await res.json()) as { ok: boolean; data: OrgDigestConfig };
      return parsed.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['digest-configs'] });
    },
  });
}

export function useRunDigest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind }: { kind: DigestKind }): Promise<{ executionId: string }> => {
      const res = await fetch(`${API_URL}/digests/run`, {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ kind }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to queue digest');
      const body = (await res.json()) as { ok: boolean; data: { executionId: string } };
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['digests'] });
    },
  });
}

export function useDigestPreferences() {
  return useQuery({
    queryKey: ['digest-preferences'],
    staleTime: 30_000,
    queryFn: async (): Promise<DigestPreferences> => {
      const res = await fetch(`${API_URL}/me/digests/preferences`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load digest preferences');
      const body = (await res.json()) as { ok: boolean; data: DigestPreferences };
      return body.data;
    },
  });
}

export function useUpdateDigestPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      kind,
      enabled,
    }: {
      kind: DigestKind;
      enabled: boolean;
    }): Promise<{ enabled: boolean }> => {
      const res = await fetch(`${API_URL}/me/digests/preferences/${kind}`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save digest preference');
      const body = (await res.json()) as { ok: boolean; data: { enabled: boolean } };
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['digest-preferences'] });
    },
  });
}
